import { effectiveRuleFor, LIVE_OFFER, OPEN_STATUSES, VisitWish, VisitWishSettings } from './visitWishApi';

export type WishFilter = 'open' | 'offer' | 'done';

/** Offer running: a table is held or offered right now. Open: still waiting. Completed: everything else. */
export const filterOf = (wish: VisitWish): WishFilter => {
  if (wish.offers?.some((offer) => LIVE_OFFER.includes(offer.status))) return 'offer';
  return OPEN_STATUSES.includes(wish.status) ? 'open' : 'done';
};

const pad = (value: string) => value.padStart(2, '0');

/** A typed date: 2026-10-30, 30.10.2026, 30.10.26 or 30.10. (any year). */
const parseDay = (query: string): { year?: string; month: string; day: string } | null => {
  const iso = query.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return { year: iso[1], month: pad(iso[2]), day: pad(iso[3]) };
  const german = query.match(/^(\d{1,2})\.(\d{1,2})\.?(\d{2}|\d{4})?$/);
  if (!german) return null;
  const year = german[3] ? (german[3].length === 2 ? `20${german[3]}` : german[3]) : undefined;
  return { year, month: pad(german[2]), day: pad(german[1]) };
};

/** No weekdays: every day. Otherwise 0 is Sunday (Date.getDay); a 7 is read as Sunday too. */
const onWishedWeekday = (wish: VisitWish, date: string) => {
  if (!wish.weekdays?.length) return true;
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
  return wish.weekdays.includes(weekday) || (weekday === 0 && wish.weekdays.includes(7));
};

/** Whether the wish applies to the date: inside its period and on one of its weekdays. */
export const coversDate = (wish: VisitWish, date: string) =>
  wish.dateFrom <= date && date <= wish.dateTo && onWishedWeekday(wish, date);

/** No time window, or one spanning the whole day: the guest is flexible in time. */
export const flexibleInTime = (wish: VisitWish) =>
  (!wish.timeFrom || wish.timeFrom <= '00:00') && (!wish.timeTo || wish.timeTo >= '23:59');

/** The wishes for one calendar day, by their wished time; flexible guests last, then first come first. */
export const wishesOnDay = (wishes: VisitWish[], date: string) =>
  wishes
    .filter((wish) => coversDate(wish, date))
    .sort(
      (a, b) =>
        Number(flexibleInTime(a)) - Number(flexibleInTime(b)) ||
        (a.timeFrom ?? '').localeCompare(b.timeFrom ?? '') ||
        a.createdAt - b.createdAt
    );

const coversDay = (wish: VisitWish, day: { year?: string; month: string; day: string }) => {
  const years = day.year
    ? [Number(day.year)]
    : Array.from(
        { length: Number(wish.dateTo.slice(0, 4)) - Number(wish.dateFrom.slice(0, 4)) + 1 },
        (_, index) => Number(wish.dateFrom.slice(0, 4)) + index
      );
  return years.some((year) => coversDate(wish, `${year}-${day.month}-${day.day}`));
};

/** The single day a search names with its year (e.g. from the calendar's link), as YYYY-MM-DD. */
export const searchedDate = (query: string) => {
  const day = parseDay(query.trim());
  return day?.year ? `${day.year}-${day.month}-${day.day}` : null;
};

/** Where a priority applies: on every day the wish covers, only on some, or on none. */
export type PriorityReach = 'all' | 'some' | 'none';

// Wishes cover at most a few months; the cap only guards against a malformed period.
const MAX_DAYS = 800;

const daysOf = (wish: VisitWish) => {
  const days: string[] = [];
  const day = new Date(`${wish.dateFrom}T12:00:00Z`);
  while (days.length < MAX_DAYS) {
    const date = day.toISOString().slice(0, 10);
    if (date > wish.dateTo) break;
    if (coversDate(wish, date)) days.push(date);
    day.setUTCDate(day.getUTCDate() + 1);
  }
  return days;
};

/**
 * Whether the wish's priority counts, by the rule in force on each day (the
 * restaurant standard or that day's exception), as the API matches it. With a
 * searched day only that day's rule decides.
 */
export const priorityReach = (wish: VisitWish, settings: VisitWishSettings, day: string | null): PriorityReach => {
  const days = day ? [day] : daysOf(wish);
  const prioritised = days.filter((date) => effectiveRuleFor(settings, date).usePriority).length;
  if (!prioritised) return 'none';
  return prioritised === days.length ? 'all' : 'some';
};

/** Matches the guest (name, email, phone) or a date within the wished period. */
export const matchesSearch = (wish: VisitWish, query: string) => {
  const text = query.trim().toLowerCase();
  if (!text) return true;
  const contact = [wish.contact.name, wish.contact.email, wish.contact.phone ?? ''].join(' ').toLowerCase();
  if (contact.includes(text)) return true;
  const day = parseDay(text);
  return !!day && coversDay(wish, day);
};
